import { useState } from 'react';

import { Box, Button, Icon, Paragraph, Tag, Text, TextButton } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconChevronDownCircleFilled,
  IconChevronUpCircleFilled,
  IconCircleFilled,
  IconExclamationCircleFilled,
} from '@zillow/constellation-icons';
import { LoadingText } from '@zg-rentals/loading-components';

import IconifiedList from '../../../../components/IconifiedList';

import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';

import InlineStyledText from '../InlineStyledText';

const { PAYMENT_SUMMARY } = STATUS_KEY;

const LIST_FONT_MAP = {
  normal: {
    textStyle: 'body-sm',
    color: 'text.subtle',
  },
  emphasize: {
    textStyle: 'body-sm-bold',
    color: 'text.subtle',
  },
  lowlight: {
    textStyle: 'body-sm',
    color: 'text.subtle',
  },
} as const;

const LIST_ICON_MAP = {
  checked: <Icon size="md" css={{ color: 'icon.feedback.success.hero' }} render={<IconCheckmarkCircleFilled />} />,
  unchecked: <Icon size="md" css={{ color: 'border.subtle' }} render={<IconCircleFilled />} />,
  alert: <Icon size="md" css={{ color: 'icon.feedback.critical.hero' }} render={<IconExclamationCircleFilled />} />,
};

const CTA_BUTTON_MAP = {
  primary_button: 'filled',
  secondary_button: 'outlined',
} as const;

type TrackerState = {
  icon: 'checked' | 'unchecked' | 'alert';
  messageText: string;
  messageStyle: 'normal' | 'emphasize' | 'lowlight';
};

type Props = {
  paymentType?: string;
  statusText: string;
  statusType: string;
  trackerStates: Array<TrackerState>;
  ctaButtons?: Array<{
    text: string;
    url: string;
    styling: 'primary_button' | 'secondary_button';
  }>;
  disclaimer?: Array<{
    text: string;
    styling?: string;
    url?: string;
  }>;
  isLoading?: boolean;
};

const PaymentTracker = ({
  isLoading = false,
  statusText,
  statusType,
  trackerStates,
  ctaButtons = [],
  disclaimer = [],
}: Props) => {
  const [isDropdownEnabled, setDropdownEnabled] = useState(false);

  const getIconifiedListItems = (list: Array<TrackerState>) =>
    list.map(({ messageText, icon, messageStyle }) => ({
      icon: LIST_ICON_MAP[icon],
      primaryContent: <Text {...LIST_FONT_MAP[messageStyle]}>{messageText}</Text>,
    }));

  return (
    <div className="PaymentTracker" data-test="connected-payments-payment-tracker">
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: isDropdownEnabled ? '600' : undefined,
        }}
      >
        {isLoading ? (
          <>
            <LoadingText textStyle="body-sm" loadingWidth="30%" isLoading={true}>
              Loading...
            </LoadingText>
            <LoadingText textStyle="body-sm-bold" loadingWidth="30%" isLoading={true}>
              Loading...
            </LoadingText>
          </>
        ) : (
          <>
            <Tag size="sm" tone={getTagTypeByKey(PAYMENT_SUMMARY, statusType)}>
              {statusText}
            </Tag>
            <TextButton
              onClick={() => setDropdownEnabled(!isDropdownEnabled)}
              textStyle="body-sm-bold"
              data-test="payment-tracker-dropdown-toggle"
              icon={isDropdownEnabled ? <IconChevronUpCircleFilled /> : <IconChevronDownCircleFilled />}
              iconPosition="end"
            >
              View details
            </TextButton>
          </>
        )}
      </Box>
      {isDropdownEnabled && (
        <div data-test="payment-tracker-dropdown">
          {trackerStates.length ? (
            <Box css={{ paddingBottom: '200' }}>
              <IconifiedList
                backgroundColor="white"
                verticalLineColor="borderLight"
                iconSize="lg"
                listItems={getIconifiedListItems(trackerStates)}
              />
            </Box>
          ) : null}
          {disclaimer ? (
            <Paragraph textStyle="body-sm" color="text.subtle" css={{ paddingBottom: ctaButtons.length ? '600' : 0 }}>
              <InlineStyledText fontColor="text.subtle" styledTextList={disclaimer} />
            </Paragraph>
          ) : null}
          {ctaButtons.length ? (
            <Box
              css={{
                display: 'flex',
                '& > .PaymentTracker-CTA-Button': {
                  paddingRight: '200',
                  '&:last-child': {
                    paddingRight: '0',
                  },
                },
              }}
            >
              {ctaButtons.map(({ text, styling, url }) => (
                <Box key={url} className="PaymentTracker-CTA-Button" css={{ flex: '1' }}>
                  <Button
                    asChild
                    fluid={true}
                    size="sm"
                    tone="brand"
                    emphasis={CTA_BUTTON_MAP[styling]}
                    css={{ whiteSpace: 'nowrap' }}
                  >
                    <a href={url}>{text}</a>
                  </Button>
                </Box>
              ))}
            </Box>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default PaymentTracker;
